import * as React from "react"
import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { cn } from "../../lib/utils"
import { ChevronLeft, ChevronRight, Calendar, X } from "lucide-react"
import { Button } from "./button"
import { getTodayString, dateToString } from "../../utils/dateUtils"

/**
 * CustomDatePicker - A mobile-friendly date picker that opens as a modal dialog
 * Avoids iOS native date picker z-index issues by using native <dialog> element
 */
export function CustomDatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = "Select date",
  className,
  disabled = false,
  error = false,
  allowFuture = false,
  label,
  "data-testid": dataTestId,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [viewDate, setViewDate] = useState(() => {
    if (value) return new Date(value)
    return new Date()
  })
  const dialogRef = useRef(null)

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const effectiveMax = max || (allowFuture ? null : getTodayString())

  // Handle dialog open/close
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    
    if (isOpen) {
      dialog.showModal()
    } else {
      dialog.close()
    }
  }, [isOpen])

  // Update viewDate when value changes
  useEffect(() => {
    if (value) {
      setViewDate(new Date(value))
    }
  }, [value])

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return ""
    // Parse the date string (YYYY-MM-DD) manually to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day) // month is 0-indexed
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    })
  }

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay()
  }

  const handlePrevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  const handleDateSelect = (day) => {
    // Create date string in YYYY-MM-DD format without timezone issues
    const year = viewDate.getFullYear()
    const month = String(viewDate.getMonth() + 1).padStart(2, '0')
    const dayStr = String(day).padStart(2, '0')
    const dateStr = `${year}-${month}-${dayStr}`
    
    // Check if date is valid
    if (min && dateStr < min) return
    if (effectiveMax && dateStr > effectiveMax) return
    
    onChange(dateStr)
    setIsOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange(null)
  }

  const isDateDisabled = (day) => {
    // Create date string in YYYY-MM-DD format without timezone issues
    const year = viewDate.getFullYear()
    const month = String(viewDate.getMonth() + 1).padStart(2, '0')
    const dayStr = String(day).padStart(2, '0')
    const dateStr = `${year}-${month}-${dayStr}`
    
    if (min && dateStr < min) return true
    if (effectiveMax && dateStr > effectiveMax) return true
    return false
  }

  const isToday = (day) => {
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day)
    return date.toDateString() === today.toDateString()
  }

  const isSelected = (day) => {
    if (!value) return false
    // Parse the value string (YYYY-MM-DD) manually to avoid timezone issues
    const [year, month, dayNum] = value.split('-').map(Number)
    return viewDate.getFullYear() === year && 
           viewDate.getMonth() === month - 1 && 
           day === dayNum
  }

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December']
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

  const daysInMonth = getDaysInMonth(viewDate.getFullYear(), viewDate.getMonth())
  const firstDay = getFirstDayOfMonth(viewDate.getFullYear(), viewDate.getMonth())

  // Generate calendar grid
  const calendarDays = []
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null)
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day)
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        data-testid={dataTestId}
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={cn(
          "h-10 px-3 rounded-xl border text-base w-full text-left flex items-center justify-between",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "bg-transparent transition-colors",
          disabled && "opacity-50 cursor-not-allowed bg-slate-100",
          error && "border-red-500",
          value ? "border-slate-900" : "border-input", // Black border when has value
          !value && "text-slate-400",
          className
        )}
      >
        <span className={value ? "text-slate-900" : "text-slate-400"}>
          {value ? formatDisplayDate(value) : placeholder}
        </span>
        <div className="flex items-center gap-2">
          {value && !disabled && (
            <span 
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-600 p-1"
            >
              <X className="w-4 h-4" />
            </span>
          )}
          <Calendar className="w-5 h-5 text-slate-400" />
        </div>
      </button>

      {/* Native Dialog Element - Properly isolates events from parent */}
      <dialog
        ref={dialogRef}
        className="fixed inset-0 m-auto p-0 bg-transparent border-none max-w-sm w-[calc(100%-2rem)] rounded-2xl shadow-2xl backdrop:bg-black/60"
        onClick={(e) => {
          // Close when clicking the backdrop
          if (e.target === dialogRef.current) {
            setIsOpen(false)
          }
        }}
      >
        <div className="bg-white rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="text-lg font-semibold text-slate-800">
              {label || "Select Date"}
            </h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-full hover:bg-slate-100 touch-manipulation"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between px-4 py-3">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-2 rounded-full hover:bg-slate-100 active:bg-slate-200 touch-manipulation"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <span className="text-base font-semibold text-slate-800">
              {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-2 rounded-full hover:bg-slate-100 active:bg-slate-200 touch-manipulation"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 px-4 pb-1">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-slate-400 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 px-4 pb-4">
            {calendarDays.map((day, index) => (
              <div key={index} className="aspect-square flex items-center justify-center">
                {day && (
                  <button
                    type="button"
                    onClick={() => !isDateDisabled(day) && handleDateSelect(day)}
                    disabled={isDateDisabled(day)}
                    className={cn(
                      "w-9 h-9 rounded-full text-sm font-medium transition-all touch-manipulation",
                      "flex items-center justify-center select-none",
                      isSelected(day) && "bg-slate-800 text-white",
                      isToday(day) && !isSelected(day) && "border-2 border-slate-800 text-slate-800",
                      !isSelected(day) && !isToday(day) && !isDateDisabled(day) && "text-slate-700 hover:bg-slate-100 active:bg-slate-200",
                      isDateDisabled(day) && "text-slate-300 cursor-not-allowed"
                    )}
                  >
                    {day}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="flex gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50">
            <Button
              type="button"
              variant="outline"
              className="flex-1 h-10 rounded-xl touch-manipulation"
              onClick={() => {
                onChange(null)
                setIsOpen(false)
              }}
            >
              Clear
            </Button>
            <Button
              type="button"
              className="flex-1 h-10 rounded-xl bg-slate-800 hover:bg-slate-700 touch-manipulation"
              onClick={() => {
                const todayStr = getTodayString()
                if (!effectiveMax || todayStr <= effectiveMax) {
                  onChange(todayStr)
                }
                setIsOpen(false)
              }}
              disabled={effectiveMax && getTodayString() > effectiveMax}
            >
              Today
            </Button>
          </div>
        </div>
      </dialog>
    </>
  )
}

export default CustomDatePicker
